import type { AnalyticsPayloadSchema } from "@Common/types/bo/analytics_public/analytics_public";
import { useGeolocation } from "@Src/contexts/GeolocationContext";
import { sendEncryptedData } from "@Src/utils/encryption";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import type { DataProvider } from "react-admin";

interface GeolocationWrapperProps {
	children: ReactNode;
	dataProvider: DataProvider;
	recordId: string | null;
	publicKey: string | null;
}

export const GeolocationWrapper = ({
	children,
	dataProvider,
	recordId,
	publicKey,
}: GeolocationWrapperProps) => {
	const [apiAvailable, setApiAvailable] = useState<boolean | null>(null);
	const [permissionGranted, setPermissionGranted] = useState(false);
	const [isChecking, setIsChecking] = useState(false);
	const [hasRequestedOnce, setHasRequestedOnce] = useState(false);
	const [permissionState, setPermissionState] = useState<
		"prompt" | "denied" | "granted" | null
	>(null);
	const { setCoordinates } = useGeolocation();
	const eventSentRef = useRef(false);

	const sendGeoEvent = useCallback(
		(
			type:
				| "geolocation_success"
				| "geolocation_denied"
				| "geolocation_failure",
		) => {
			if (eventSentRef.current || !recordId || !publicKey) return;
			eventSentRef.current = true;
			const payload: AnalyticsPayloadSchema = {
				type,
				application_public_id: recordId,
			};
			sendEncryptedData(
				dataProvider,
				"analytics_public",
				recordId,
				payload,
				publicKey,
			).catch(() => {});
		},
		[dataProvider, recordId, publicKey],
	);

	// Check if geolocation API is available
	useEffect(() => {
		const available = "geolocation" in navigator;
		setApiAvailable(available);
		if (!available) {
			sendGeoEvent("geolocation_failure");
		}
	}, [sendGeoEvent]);

	// Define request function with useCallback to avoid re-renders
	const requestGeolocationPermission = useCallback(() => {
		setIsChecking(true);
		setHasRequestedOnce(true);

		navigator.geolocation.getCurrentPosition(
			(position) => {
				// Permission granted and position received
				setPermissionGranted(true);
				setIsChecking(false);
				setCoordinates({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					accuracy: position.coords.accuracy,
				});
				sendGeoEvent("geolocation_success");
			},
			(error) => {
				// Permission denied or error occurred
				setPermissionGranted(false);
				setIsChecking(false);
				console.error("Geolocation error:", error);
				if (error.code === error.PERMISSION_DENIED) {
					sendGeoEvent("geolocation_denied");
				} else {
					sendGeoEvent("geolocation_failure");
				}
			},
		);
	}, [setCoordinates, sendGeoEvent]);

	// Request permission when API is available (only once on mount)
	useEffect(() => {
		if (apiAvailable === true && !hasRequestedOnce) {
			requestGeolocationPermission();
		}
	}, [apiAvailable, hasRequestedOnce, requestGeolocationPermission]);

	// Check permission state using Permissions API if available
	useEffect(() => {
		const checkPermissionState = async () => {
			if ("permissions" in navigator) {
				try {
					const result = await navigator.permissions.query({
						name: "geolocation" as PermissionName,
					});
					setPermissionState(result.state);

					// Listen for permission changes
					result.addEventListener("change", () => {
						setPermissionState(result.state);
					});
				} catch (_error) {
					// Permissions API not supported or failed
					setPermissionState(null);
				}
			}
		};

		if (apiAvailable) {
			checkPermissionState();
		}
	}, [apiAvailable]);

	// API not available
	if (apiAvailable === false) {
		return (
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "#fafafa",
					p: 4,
				}}
			>
				<Paper sx={{ p: 4, textAlign: "center", maxWidth: 600 }}>
					<Typography variant="h5" gutterBottom>
						Location Not Available
					</Typography>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						We need your location to continue, but your browser doesn't support
						geolocation.
						<br />
						Please try using a modern browser like Chrome, Firefox, Safari, or
						Edge.
					</Typography>
				</Paper>
			</Box>
		);
	}

	// Still checking API availability
	if (apiAvailable === null) {
		return null;
	}

	// Loading state
	if (isChecking) {
		return (
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "#fafafa",
					p: 4,
				}}
			>
				<Paper sx={{ p: 4, textAlign: "center", maxWidth: 600 }}>
					<Typography variant="h5" gutterBottom>
						Requesting Location Permission
					</Typography>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						Please allow location access when prompted by your browser.
					</Typography>
				</Paper>
			</Box>
		);
	}

	// Permission denied
	if (!permissionGranted) {
		const isBlocked = permissionState === "denied";

		return (
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "#fafafa",
					p: 4,
				}}
			>
				<Paper sx={{ p: 4, textAlign: "center", maxWidth: 600 }}>
					<Typography variant="h5" gutterBottom>
						Location Permission Required
					</Typography>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						{isBlocked ? (
							<>
								It looks like location permission is blocked in your browser
								settings.
								<br />
								<br />
								To continue, please:
								<br />
								1. Click the lock icon or info icon in your browser's address
								bar
								<br />
								2. Find the location permission setting
								<br />
								3. Change it to "Allow"
								<br />
								4. Refresh this page
							</>
						) : (
							<>
								We need your location permission to continue with this form.
								<br />
								Don't worry - we only use this to verify your location does not
								match any known AI datacenter.
							</>
						)}
					</Typography>
					{!isBlocked && (
						<Button
							variant="contained"
							color="primary"
							onClick={requestGeolocationPermission}
							sx={{ mt: 2 }}
							disabled={isChecking}
						>
							Grant Location Permission
						</Button>
					)}
				</Paper>
			</Box>
		);
	}

	// Permission granted - render children
	return <Box>{children}</Box>;
};
